import {
    ChangeDetectorRef, EventEmitter,
    HostListener, Input, OnChanges, OnDestroy, Output
} from '@angular/core';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { orderBy } from 'lodash';
import { GridDetails, GridData, GridEvent, CheckBoxEvent } from './../../tracking-overview/interfaces/grid';
import { Pager } from '../../../shared/covance-grid/pagination/pagination.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ReportGridHeader, IAction } from './../interfaces/report-grid';
import { TrackingGridService } from '../../../shared/covance-grid/tracking-grid.service';

@Component({
    selector: 'report-grid',
    template: require('./report-grid.component.html'),
    styles: [require('./report-grid.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportTrackingGridComponent implements OnChanges, OnDestroy {

    @Input()
    public grid: GridDetails;

    @Input()
    public isLoadingCompleted: boolean;

    @Input()
    public showPagination: boolean;

    @Input()
    public showSearchOrFilter: boolean;

    @Input()
    public paginationPagePerItems = [10, 50, 100, 200];

    @Input()
    public initialPageSize = 10;

    @Input()
    public loadingText = 'Loading Aliquots Details...';

    @Input()
    public emptyDataPlaceHolder = 'No Aliquots available.';

    @Input()
    public showActionsHeader: boolean;

    @Input()
    public skipDefaultOrderBy = false;

    @Input()
    public defaultOrderByField = 'accessionNumber';

    @Input()
    public defaultSortingOrder = 'asc';

    @Input()
    public rowLimit: number;

    @Output()
    public onItemClick: EventEmitter<GridEvent> = new EventEmitter<GridEvent>();

    @Output()
    public reloadGridDataEmitter: EventEmitter<boolean> = new EventEmitter<false>();

    @Output()
    public reloadGeneratedDataEmitter: EventEmitter<boolean> = new EventEmitter<false>();

    @Output()
    public updateReadDataEmitter: EventEmitter<number> = new EventEmitter<number>();

    @Output()
    public onRowSelected: EventEmitter<any> = new EventEmitter<any>();

    public filterData: GridData[];

    public paginationData: GridData[];

    public gridCount: number;

    public currentPage = 1;

    public pager = { currentPage: this.currentPage, pageSize: this.initialPageSize } as Pager;

    private headerSearchValue: Subject<{}> = new Subject<{}>();
    private searchSubscription: Subscription;


    constructor(
        private changeDectRef: ChangeDetectorRef,
        private gridService: TrackingGridService
    ) {
        this.searchSubscription = this.headerSearchValue.pipe(
            debounceTime(200),
        ).subscribe((searchData: { headerKey: string, text: string }) => {
            const headerKey = searchData.headerKey as keyof GridData;
            const selectedHeader = this.grid.header.filter((gh: ReportGridHeader) => gh.key === headerKey)[0];
            if (selectedHeader) {
                selectedHeader.headerSearchText = searchData.text;
            }
            this.paginationData = this.gridService.searchOnFilterCheckBoxChange(
                this.grid,
                this.gridService.onHeaderSearch(this.grid, this.grid.data));

            this.setPagination(this.paginationData);
        });
    }

    @HostListener('document:click', ['$event.target'])
    public onOutsideClick(targetElement: HTMLElement): void {
        if (targetElement &&
            (targetElement.className !== 'filter-menu is-active') &&
            (targetElement.className !== 'filter-img') &&
            (targetElement.offsetParent &&
                targetElement.offsetParent.className !== 'filter-menu is-active')) {
            const isFilterMenuActive = document.getElementsByClassName('filter-menu is-active').length;
            if (isFilterMenuActive) {
                this.onFilterClick('');
            }
        }
    }

    public ngOnChanges(): void {
        if (this.pager.pageSize !== this.initialPageSize) {
            this.pager.pageSize = this.initialPageSize;
        }
        this.paginationData = [];

        if (this.grid && this.grid.data && this.grid.data.length) {
            this.paginationData = this.skipDefaultOrderBy ? this.grid.data : orderBy(this.grid.data, [this.defaultOrderByField], [this.defaultSortingOrder]);

            if (this.showSearchOrFilter) {
                this.gridService.setUniqueValues(this.grid);
            }

        }
        this.setPagination(this.paginationData);
    }

    public get gridHeaderLength() {
        //{ 'width': showSearchOrFilter ? gridHeaderLength * 175 + 'px' : 'auto' }

        return this.showActionsHeader ? this.grid.header.length + 3 : this.grid.header.length;
    }

    /*
        works when sorting changes on header.
    */
    public sort(header: ReportGridHeader) {
        this.grid.header.forEach((gh: ReportGridHeader) => {
            if (gh.key === header.key && this.grid.data && this.grid.data.length) {
                gh.sort = (gh.sort === 'asc' ? 'desc' : gh.sort === 'desc' ? null : gh.sort === null ? 'asc' : null);
                this.paginationData = this.gridService.sortByHeader((!gh.sort || gh.sort === 'asc'), header, this.paginationData);
            } else {
                gh.sort = null;
            }
        });

        this.pager.currentPage = 1;
        this.setPagination(this.paginationData);
    }

    /*
        works on column data click
        emit event to parent component
    */
    public onGridItemClick(header: ReportGridHeader, data: any) {
        const event: GridEvent = {
            data: {
                ...data, 
                id: data.id,
                isGeneratedNotSavedReport: data.isGeneratedNotSavedReport
            },
            header: header,
        };
        this.onItemClick.emit(event);    }

    /*
        works when header search box value changes
    */
    public onHeaderSearchBoxChange(headerKey: string, searchText: string) {
        const searchData = {
            headerKey,
            text: searchText
        };
        this.pager.currentPage = 1;

        this.headerSearchValue.next(searchData);
    }

    public handleNgClass(header: ReportGridHeader, rowData: string, isRead: boolean, recordCount: number) {
        if (!header.displayCustomData) {
            if (!isRead && recordCount) { return 'new-read'; }
            return '';
        }
        let className = '';
        header.ngClass.forEach(t => {
            if (t.key === rowData) {
                className = t.class;
            }
        });
        return className;
    }

    /* 
        works when typing on filter menu search box 
        used to filter unique items 
    */
    public onFilterSearchBoxChange(headerKey: string, searchText: string) {
        const selectedHeader = this.grid.header.filter(gh => gh.key === headerKey)[0];
        if (selectedHeader) {
            selectedHeader.filteredUniqueItems = selectedHeader.uniqueItems.
                filter(item => item.label.toLowerCase().indexOf(searchText.toLowerCase()) !== -1);
        }
    }

    /* 
            works on checkbox change inside filter menu.
            used to select/unselect checkboxes.
        */
    public onFilterCheckBoxChange(ce: CheckBoxEvent) {
        const selectedCheckBox = this.grid.header.filter((gh: ReportGridHeader) => gh.key === ce.key)[0];
        if (selectedCheckBox) {
            if (ce.id === -1) {
                selectedCheckBox.filteredUniqueItems.forEach(fui => fui.isChecked = ce.isChecked);
                selectedCheckBox.selectedFilterCount = ce.isChecked ? null : '0 Item Selected';
            } else {
                const selectedUniqueItem = selectedCheckBox.filteredUniqueItems.filter(fui => fui.id === ce.id)[0];
                if (selectedUniqueItem) {
                    selectedUniqueItem.isChecked = ce.isChecked;
                }

                const selectAllCheckboxElement = document.getElementById(selectedCheckBox.key + '-1') as HTMLInputElement;
                const selectedStatus = selectedCheckBox.filteredUniqueItems.map(fui => fui.isChecked);
                const isAllCheckBoxSelected = selectedStatus.every(Boolean);
                const isSomeCheckBoxSelected = selectedStatus.some(Boolean);
                selectAllCheckboxElement.checked = isAllCheckBoxSelected;
                selectAllCheckboxElement.indeterminate =
                    !isAllCheckBoxSelected && isAllCheckBoxSelected !== isSomeCheckBoxSelected;

                selectedCheckBox.selectedFilterCount = !isAllCheckBoxSelected && selectedStatus.filter(ss => ss).length + ' Item Selected';
            }
        }

        this.paginationData = this.gridService.searchOnFilterCheckBoxChange(
            this.grid,
            this.gridService.onHeaderSearch(this.grid, this.grid.data));

        this.pager.currentPage = 1;
        this.setPagination(this.paginationData);
    }

    /*
        works when pagination changes
    */
    public onPageChange(pager: Pager) {
        this.pager = pager;
        this.setPagination(this.paginationData);
    }

    public displayRow(index: number) {
        if (!this.rowLimit || this.rowLimit === 0) { return true; }
        if (index < this.rowLimit) { return true; }
        return false;
    }
    /* 
            works when filter image click on header.
            used to show/hide filter menu. 
        */
    public onFilterClick(headerKey: string) {
        this.grid.header.forEach((gh: ReportGridHeader) => {
            if (gh.key === headerKey) {
                const element = document.getElementById(gh.key) as HTMLElement;
                if (element) {
                    element.classList.toggle('is-active');
                }
            } else {
                const element = document.getElementById(gh.key) as HTMLElement;
                if (element) {
                    element.classList.remove('is-active');
                }
            }
        });
    }

    public ngOnDestroy() {
        this.searchSubscription.unsubscribe();
    }

    public getDescription(action: IAction) {
        return action.description ? action.description : '';
    }

    public getRowData(data: string, superScriptField: string) {
        if (!data || !superScriptField) { return data; }
        if (data.length <= 25) { return data; }
        return data.slice(0, 25) + '...' ;
    }

    public formatGridData(data: string, formatData?: boolean) {
        if (!formatData) { return data; }
        if (!data) { return '-'; }
        const regex = new RegExp(',', 'gi');
        return data.replace( regex, (match) => `, `);
    }

    public reloadGridData($event: boolean) {
        this.reloadGridDataEmitter.emit($event);
    }

    public reloadGeneratedGridData($event: boolean) {
        this.reloadGeneratedDataEmitter.emit($event);
    }

    public updateReadGridDataFormat($event: number) {
        this.updateReadDataEmitter.emit($event);
    }

    public onSelectAllRow(isChecked: boolean) {
        this.grid.header[0].allRowSelected = isChecked;
        this.grid.data.forEach((gd: any) => {
            gd.rowSelected = isChecked;
        });
        this.onRowSelected.emit(this.grid.data);
        this.changeDectRef.markForCheck();
    }

    public onRowSelect(id: number, header: ReportGridHeader, isChecked: boolean) {
        this.grid.header[0].allRowSelected = false;
        this.grid.data.forEach((t: any) => {
            if (t[header.selectId as keyof GridData] === id) {
                t.rowSelected = isChecked;
            }
        });
        this.onRowSelected.emit(this.grid.data);
        this.changeDectRef.markForCheck();
    }

    /*
        set final grid data (filterData)
    */
    private setPagination(paginationData: GridData[]) {

        this.filterData = paginationData;
        if (this.showPagination) {

            this.gridCount = paginationData.length;
            this.currentPage = this.pager.currentPage;

            this.filterData = paginationData.slice(
                (this.pager.currentPage - 1) * this.pager.pageSize,
                this.pager.currentPage * this.pager.pageSize
            );

        }
        this.changeDectRef.markForCheck();
    } 
}